/**
 * Project Standards Selector Component
 *
 * Allows selection of multiple development standards for a project
 * Displays available standards from .equilateral-standards/
 * Manages project-level standard assignments
 *
 * Features:
 * - Browse available standards by category
 * - Multi-select standards for project
 * - Mark standards as required vs optional
 * - Save standards configuration
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Checkbox, Alert, Spinner, Badge, Label } from 'flowbite-react';
import { HiCheckCircle, HiDocumentText, HiSave } from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface ProjectStandardsSelectorProps {
  clientId: string;
  companyId: string;
  projectId: string;
  onUpdate?: () => void;
}

interface Standard {
  category: string;
  path: string;
  name: string;
  description?: string;
}

interface ProjectStandard {
  Standard_Category: string;
  Standard_Path: string;
  Is_Required: boolean;
}

// Available standards categories
const AVAILABLE_STANDARDS: Standard[] = [
  {
    category: 'Backend Development',
    path: '.equilateral-standards/serverless-saas-aws/backend_handler_standards.md',
    name: 'Backend Handler Standards',
    description: 'Lambda handler patterns, database operations, response formatting'
  },
  {
    category: 'Backend Development',
    path: '.equilateral-standards/serverless-saas-aws/api_standards.md',
    name: 'API Standards',
    description: 'API response formats, error handling, endpoint conventions'
  },
  {
    category: 'Backend Development',
    path: '.equilateral-standards/serverless-saas-aws/lambda_database_standards.md',
    name: 'Lambda Database Standards',
    description: 'Database connection patterns, query optimization, connection pooling'
  },
  {
    category: 'Frontend Development',
    path: '.equilateral-standards/frontend-development/frontend_standards.md',
    name: 'Frontend Development Standards',
    description: 'React patterns, TypeScript conventions, component architecture'
  },
  {
    category: 'Security & Compliance',
    path: '.equilateral-standards/compliance-security/credential_management_standards.md',
    name: 'Credential Management Standards',
    description: 'Secure credential storage, encryption, access control'
  },
  {
    category: 'Infrastructure',
    path: '.equilateral-standards/serverless-saas-aws/api_gateway_cors_standards.md',
    name: 'API Gateway CORS Standards',
    description: 'CORS configuration, authentication, preflight handling'
  },
  {
    category: 'Infrastructure',
    path: '.equilateral-standards/serverless-saas-aws/cost_optimization_standards.md',
    name: 'Cost Optimization Standards',
    description: 'Infrastructure sizing, pay-per-use patterns, ARM64 optimization'
  },
  {
    category: 'Data & Database',
    path: '.equilateral-standards/database-patterns/schema_standards.md',
    name: 'Database Schema Standards',
    description: 'Table design, indexing, relationships, multi-tenancy'
  },
  {
    category: 'Multi-Agent',
    path: '.equilateral-standards/multi-agent-orchestration/agent_orchestration_standards.md',
    name: 'Agent Orchestration Standards',
    description: 'Multi-agent workflows, agent communication, state management'
  },
  {
    category: 'Development Process',
    path: '.equilateral-standards/foundation/equilateral_development_principles.md',
    name: 'Development Principles',
    description: 'Core principles, error-first design, cost-first infrastructure'
  }
];

export const ProjectStandardsSelector: React.FC<ProjectStandardsSelectorProps> = ({
  clientId,
  companyId,
  projectId,
  onUpdate
}) => {
  const [selectedStandards, setSelectedStandards] = useState<Map<string, boolean>>(new Map());
  const [requiredStandards, setRequiredStandards] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadProjectStandards = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call<ProjectStandard>(
        API_ENDPOINTS.PROJECTS.STANDARDS,
        'GET',
        undefined,
        { params: { Project_ID: projectId, Client_ID: clientId, Company_ID: companyId } }
      );

      if (isSuccessResponse(response)) {
        if (response.data?.Records) {
          const selected = new Map<string, boolean>();
          const required = new Set<string>();

          response.data.Records.forEach((std: ProjectStandard) => {
            selected.set(std.Standard_Path, true);
            if (std.Is_Required) {
              required.add(std.Standard_Path);
            }
          });

          setSelectedStandards(selected);
          setRequiredStandards(required);
        }
      } else {
        // No standards configured yet - not an error for 404
        setSelectedStandards(new Map());
        setRequiredStandards(new Set());
      }
    } catch (err) {
      console.error('[ProjectStandardsSelector] Error loading standards:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project standards');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, companyId, projectId]);

  useEffect(() => {
    loadProjectStandards();
  }, [loadProjectStandards]);

  const handleToggleStandard = useCallback((path: string) => {
    setSelectedStandards(prev => {
      const newMap = new Map(prev);
      if (newMap.has(path)) {
        newMap.delete(path);
        // Also remove from required if unchecked
        setRequiredStandards(prevRequired => {
          const newRequired = new Set(prevRequired);
          newRequired.delete(path);
          return newRequired;
        });
      } else {
        newMap.set(path, true);
      }
      return newMap;
    });
  }, []);

  const handleToggleRequired = useCallback((path: string) => {
    setRequiredStandards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      const standards: ProjectStandard[] = Array.from(selectedStandards.keys()).map(path => {
        const standard = AVAILABLE_STANDARDS.find(s => s.path === path);
        return {
          Standard_Category: standard?.category || 'Uncategorized',
          Standard_Path: path,
          Is_Required: requiredStandards.has(path)
        };
      });

      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.PROJECTS.STANDARDS,
        'POST',
        {
          Project_ID: projectId,
          Client_ID: clientId,
          Company_ID: companyId,
          Standards: standards
        }
      );

      if (isSuccessResponse(response)) {
        setSuccessMessage('Project standards saved successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        onUpdate?.();
      } else {
        throw new Error(response.message || 'Failed to save project standards');
      }
    } catch (err) {
      console.error('[ProjectStandardsSelector] Error saving standards:', err);
      setError(err instanceof Error ? err.message : 'Failed to save project standards');
    } finally {
      setIsSaving(false);
    }
  }, [clientId, companyId, projectId, selectedStandards, requiredStandards, onUpdate]);

  // Group standards by category
  const groupedStandards = AVAILABLE_STANDARDS.reduce((acc, standard) => {
    if (!acc[standard.category]) {
      acc[standard.category] = [];
    }
    acc[standard.category].push(standard);
    return acc;
  }, {} as Record<string, Standard[]>);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" icon={HiCheckCircle} onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-600">
            {selectedStandards.size} standards selected ({requiredStandards.size} required)
          </p>
        </div>
        <Button
          color="blue"
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            <>
              <HiSave className="w-4 h-4 mr-2" />
              Save Standards
            </>
          )}
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedStandards).map(([category, standards]) => (
          <Card key={category}>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <HiDocumentText className="w-5 h-5 mr-2 text-blue-600" />
              {category}
            </h4>

            <div className="space-y-3">
              {standards.map((standard) => {
                const isSelected = selectedStandards.has(standard.path);
                const isRequired = requiredStandards.has(standard.path);

                return (
                  <div
                    key={standard.path}
                    className={`p-3 border rounded-lg transition-all ${
                      isSelected
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id={`standard-${standard.path}`}
                        checked={isSelected}
                        onChange={() => handleToggleStandard(standard.path)}
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor={`standard-${standard.path}`}
                          className="font-medium text-gray-900 cursor-pointer"
                        >
                          {standard.name}
                          {isRequired && (
                            <Badge color="failure" size="sm" className="ml-2">
                              Required
                            </Badge>
                          )}
                        </Label>
                        {standard.description && (
                          <p className="text-xs text-gray-600 mt-1">
                            {standard.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1 font-mono">
                          {standard.path}
                        </p>

                        {isSelected && (
                          <div className="mt-2">
                            <label htmlFor={`required-${standard.path}`} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                id={`required-${standard.path}`}
                                checked={isRequired}
                                onChange={() => handleToggleRequired(standard.path)}
                              />
                              <span>Mark as required for this project</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs text-gray-700">
          <strong>Note:</strong> Selected standards will be used for code generation,
          validation, and documentation. Required standards will be enforced in CI/CD pipelines.
        </p>
      </div>
    </div>
  );
};

export default ProjectStandardsSelector;
